import {
  computeTileSizeChange,
  ORDERED_TILE_FONT_SIZE_VARIANTS,
  TILE_DEFAULT_FONT_SIZE_VARIANT,
} from '@/components/diagrams/components/Tile';
import {
  type ArrowEdgeStyleVariant,
  DiagramEdgeArrowheads,
  DiagramEdgeLineStyle,
  DiagramEdgeRoute,
  DiagramNodeDisplay,
  DiagramNodeFill,
  type DiagramNodeIcon,
  DiagramNodeShape,
  GraphVizIconShape,
  GraphVizNodeComponentType,
  GraphVizTileEmphasis,
} from '@/components/diagrams/diagramStyle';
import {
  type AlignKind,
  computeAlignmentChanges,
  computeDistributionChanges,
  type DistributeAxis,
  type NodePositionUpdate,
} from '@/components/diagrams/FlowChart/utils/alignment';
import { DiagramNodeKind } from '@/types/schema';

import { useDiagramEditing } from '../contexts/diagramEditing.context';
import {
  type DiagramBuilderEdgeStyle,
  type DiagramBuilderNodeStyle,
} from '../utils/diagram';
import {
  editDiagramNodeStyles,
  getTextVisibilityStyle,
} from '../utils/diagramEdits';
import { getDiagramNodeIcon } from '../utils/diagramTiles';

/** The width of a line without a saved width, in canvas pixels. */
const DEFAULT_LINE_WIDTH = 1;

/** Returns the value that every item has, or the fallback marked as mixed. */
function sharedValue<T>(values: T[], fallback: T) {
  const first = values[0] ?? fallback;
  return { value: first, isMixed: values.some((value) => value !== first) };
}

/** Returns the shared value, or null when the items have different values. */
function nullWhenMixed<T>({ value, isMixed }: { value: T; isMixed: boolean }) {
  return isMixed ? null : value;
}

/**
 * Returns the nodes and connections that the user selected on the canvas, and
 * actions that edit all of them. React Flow owns the selection.
 */
export function useDiagramSelection() {
  const { diagram, update, reactFlowDiagram, openLabelEditor, removeElements } =
    useDiagramEditing();
  const { content } = diagram;
  const selectedIDs = new Set(
    [...reactFlowDiagram.nodes, ...reactFlowDiagram.edges]
      .filter((element) => element.selected)
      .map((element) => element.id)
  );
  const nodes = content.nodes.filter((node) => selectedIDs.has(node.nodeID));
  const edges = content.edges.filter((edge) => selectedIDs.has(edge.edgeID));
  // The tile data has the resolved style: the override, or the default of the
  // node kind. The icon and its shape apply only to icons. The shape and its
  // fill apply only to shapes. Emphasis, notes, and footers apply only to tiles.
  const tiles = reactFlowDiagram.nodes
    .filter((node) => selectedIDs.has(node.id))
    .map((node) => node.data);
  // Standalone text has no display, color, or other node style.
  const styledTiles = tiles.filter(
    (tile) => tile.item.kind !== DiagramNodeKind.Text
  );
  const byDisplay = (display: DiagramNodeDisplay) =>
    styledTiles.filter((tile) => tile.componentType === display);
  const iconTiles = byDisplay(DiagramNodeDisplay.TileIcon);
  const shapeTiles = byDisplay(DiagramNodeDisplay.Shape);
  const otherTiles = byDisplay(DiagramNodeDisplay.Tile);
  // Tiles and icons have an emphasis: the shade of the body or of the icon.
  const emphasisTiles = [...otherTiles, ...iconTiles];

  /** Sets the style overrides of the nodes of `targets`. */
  const editNodeStyles = (
    targets: typeof tiles,
    changes: Partial<DiagramBuilderNodeStyle>
  ) => {
    const ids = new Set(targets.map((tile) => tile.item.nodeID));
    update(editDiagramNodeStyles(diagram, ids, changes));
  };

  // Align needs two nodes. Distribute needs three.
  const selectedFlowNodes = reactFlowDiagram.nodes.filter((node) =>
    selectedIDs.has(node.id)
  );
  const alignable = selectedFlowNodes.map((node) => ({
    id: node.id,
    position: node.position,
    width: node.measured?.width ?? node.width ?? 0,
    height: node.measured?.height ?? node.height ?? 0,
  }));
  /** Saves the moved positions as one edit. */
  const moveNodes = (updates: NodePositionUpdate[]) => {
    if (updates.length === 0) return;
    const positions = new Map(
      updates.map(({ id, position }) => [
        id,
        { x: Math.round(position.x), y: Math.round(position.y) },
      ])
    );
    update({
      ...diagram,
      content: {
        ...content,
        // A moved node has new connection ends, so its saved bends no longer fit.
        edges: content.edges.map((edge) =>
          positions.has(edge.sourceNodeID) || positions.has(edge.targetNodeID)
            ? { ...edge, waypoints: null }
            : edge
        ),
        nodes: content.nodes.map((node) => ({
          ...node,
          position: positions.get(node.nodeID) ?? node.position,
        })),
      },
    });
  };

  /**
   * Makes each selected node one size step larger or smaller: the next font
   * size and a proportional width. The height follows the content again.
   * Standalone text keeps its size.
   */
  const resizeNodes = (increment: boolean) => {
    const sizes = new Map(
      selectedFlowNodes.flatMap((node) => {
        if (node.data.item.kind === DiagramNodeKind.Text) return [];
        const change = computeTileSizeChange(
          node.measured?.width ?? node.width ?? 0,
          node.data.fontSizeVariant,
          increment
        );
        return change ? [[node.id, change] as const] : [];
      })
    );
    if (sizes.size === 0) return;
    update({
      ...diagram,
      content: {
        ...content,
        nodes: content.nodes.map((node) => {
          const change = sizes.get(node.nodeID);
          if (!change) return node;
          return {
            ...node,
            nodeStyle: {
              ...node.nodeStyle,
              fontSize: change.newFontSizeVariant,
              width: Math.round(change.newWidth),
              height: null,
            },
          };
        }),
      },
    });
  };
  const sharedColor = sharedValue(
    styledTiles.map((tile) => tile.item.nodeStyle?.color ?? null),
    null
  );
  // Standalone text has no size setting.
  const fontSizes = styledTiles.map(
    (tile) => tile.fontSizeVariant ?? TILE_DEFAULT_FONT_SIZE_VARIANT
  );

  /**
   * Sets the style overrides of every selected connection. A new route shape
   * also removes the saved bends, which fit only the old shape.
   */
  const editEdgeStyles = (changes: Partial<DiagramBuilderEdgeStyle>) => {
    const ids = new Set(edges.map((edge) => edge.edgeID));
    update({
      ...diagram,
      content: {
        ...content,
        edges: content.edges.map((edge) =>
          ids.has(edge.edgeID)
            ? {
                ...edge,
                ...(changes.route && { waypoints: null }),
                edgeStyle: { ...edge.edgeStyle, ...changes },
              }
            : edge
        ),
      },
    });
  };
  /** Returns the shared value of a style of the selected connections, or null. */
  const edgeStyleValue = <K extends keyof DiagramBuilderEdgeStyle>(
    key: K,
    fallback: NonNullable<DiagramBuilderEdgeStyle[K]>
  ) => {
    return nullWhenMixed(
      sharedValue(
        edges.map((edge) => edge.edgeStyle?.[key] ?? fallback),
        fallback
      )
    );
  };

  // Label actions apply when the selection is exactly one connection.
  const [singleEdge] = edges.length === 1 ? edges : [];

  return {
    selectedCount: nodes.length + edges.length,
    /** The IDs of the selected nodes. */
    nodeIDs: nodes.map((node) => node.nodeID),
    /** The IDs of the selected connections. */
    edgeIDs: edges.map((edge) => edge.edgeID),
    hasNodes: nodes.length > 0,
    hasStyledNodes: styledTiles.length > 0,
    hasIconNodes: iconTiles.length > 0,
    hasShapeNodes: shapeTiles.length > 0,
    hasTileNodes: otherTiles.length > 0,
    hasEmphasisNodes: emphasisTiles.length > 0,
    display: sharedValue(
      styledTiles.map(
        (tile) => tile.componentType ?? GraphVizNodeComponentType.Tile
      ),
      GraphVizNodeComponentType.Tile
    ),
    shape: sharedValue(
      iconTiles.map((tile) => tile.iconShape ?? GraphVizIconShape.Circle),
      GraphVizIconShape.Circle
    ),
    emphasis: sharedValue(
      emphasisTiles.map(
        (tile) => tile.tileEmphasis ?? GraphVizTileEmphasis.Low
      ),
      GraphVizTileEmphasis.Low
    ),
    /**
     * Draws the selected nodes as tiles, icons, or shapes. Each display has
     * its own default size, so the change also removes the saved size.
     */
    setDisplay: (display: GraphVizNodeComponentType) =>
      editNodeStyles(styledTiles, { display, width: null, height: null }),
    /** The shared glyph of the selected icons. Null when unset or mixed. */
    icon: nullWhenMixed(
      sharedValue(
        iconTiles.map((tile) => getDiagramNodeIcon(tile.item) ?? null),
        null
      )
    ) as DiagramNodeIcon | null,
    setIcon: (icon: DiagramNodeIcon) => editNodeStyles(iconTiles, { icon }),
    /** The shared shape and fill of the selected shapes. Null when mixed. */
    nodeShape: nullWhenMixed(
      sharedValue(
        shapeTiles.map(
          (tile) => tile.item.nodeStyle?.shape ?? DiagramNodeShape.Rounded
        ),
        DiagramNodeShape.Rounded
      )
    ) as DiagramNodeShape | null,
    setNodeShape: (shape: DiagramNodeShape) =>
      editNodeStyles(shapeTiles, { shape }),
    fill: nullWhenMixed(
      sharedValue(
        shapeTiles.map(
          (tile) => tile.item.nodeStyle?.fill ?? DiagramNodeFill.Filled
        ),
        DiagramNodeFill.Filled
      )
    ) as DiagramNodeFill | null,
    setFill: (fill: DiagramNodeFill) => editNodeStyles(shapeTiles, { fill }),
    setIconShape: (iconShape: GraphVizIconShape) =>
      editNodeStyles(iconTiles, { iconShape }),
    setTileEmphasis: (emphasis: GraphVizTileEmphasis) =>
      editNodeStyles(emphasisTiles, { emphasis }),
    /**
     * The shared saved color of the selected nodes: a palette token or a hex
     * color. Null when the nodes have different colors or no saved color.
     */
    color: nullWhenMixed(sharedColor),
    /** True when every selected tile shows its notes or its footer. */
    showsNotes: otherTiles.every((tile) => !tile.item.nodeStyle?.hideDetail),
    showsFooter: otherTiles.every(
      (tile) => !tile.item.nodeStyle?.hideFooterLabel
    ),
    /** Shows or hides the notes or the footer of every selected tile. */
    setTextVisibility: (field: 'notes' | 'footer', show: boolean) =>
      editNodeStyles(otherTiles, getTextVisibilityStyle(field, show)),
    /** Saves a palette token or a hex color on every selected node. */
    setColor: (color: string) => editNodeStyles(styledTiles, { color }),
    canIncreaseSize: fontSizes.some(
      (size) => size !== ORDERED_TILE_FONT_SIZE_VARIANTS.at(-1)
    ),
    canDecreaseSize: fontSizes.some(
      (size) => size !== ORDERED_TILE_FONT_SIZE_VARIANTS[0]
    ),
    increaseSize: () => resizeNodes(true),
    decreaseSize: () => resizeNodes(false),
    canAlign: alignable.length >= 2,
    canDistribute: alignable.length >= 3,
    align: (kind: AlignKind) =>
      moveNodes(computeAlignmentChanges(alignable, kind)),
    distribute: (axis: DistributeAxis) =>
      moveNodes(computeDistributionChanges(alignable, axis)),
    hasEdges: edges.length > 0,
    /** The shared styles of the selected connections. Null when they differ. */
    edgeStyle: {
      route: edgeStyleValue('route', DiagramEdgeRoute.Elbow),
      lineStyle: edgeStyleValue(
        'lineStyle',
        DiagramEdgeLineStyle.Solid
      ) as ArrowEdgeStyleVariant | null,
      strokeWidth: edgeStyleValue('strokeWidth', DEFAULT_LINE_WIDTH),
      arrowheads: edgeStyleValue(
        'arrowheads',
        DiagramEdgeArrowheads.End
      ) as DiagramEdgeArrowheads | null,
      color: edgeStyleValue('color', ''),
    },
    /** True when every selected connection shows label text. */
    showsEdgeLabels: edges.every(
      (edge) => !!edge.label && !edge.edgeStyle?.hideLabel
    ),
    /**
     * Shows or hides the labels of the selected connections. A connection
     * keeps its label text while its label is hidden. To show the label of
     * one connection without text, the user writes the label in place.
     */
    setShowsEdgeLabels: (show: boolean) => {
      if (show && singleEdge && !singleEdge.label) {
        openLabelEditor({ kind: 'edge', id: singleEdge.edgeID });
      }
      editEdgeStyles({ hideLabel: !show });
    },
    setEdgeStyle: editEdgeStyles,
    /** Removes the selection and the connections attached to it. */
    removeSelection: () => removeElements(...selectedIDs),
  };
}
